import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { typeLabel } from '@/mocks/films';
import { films } from '@/mocks/films';

const slides = ['chang-ye-xing-he', 'er-shi-si-xiao-shi', 'gu-cheng-mi-an', 'di-shang-zhi-cheng']
  .map((id) => films.find((film) => film.id === id))
  .filter((film): film is NonNullable<typeof film> => Boolean(film));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  if (slides.length === 0) return null;
  const current = slides[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点影视轮播"
    >
      <div className="relative h-[460px] w-full sm:h-[480px] md:h-[560px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={slide.backdrop}
              alt={`${slide.title}剧照`}
              title={`${slide.title}剧情介绍 - 同学的妈妈`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/70 to-foreground-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

        <div className="relative z-10 mx-auto flex h-full w-full items-end px-4 pb-10 md:px-8 md:pb-14 lg:px-12">
          <div key={current.id} className="w-full anim-fade-up">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold tracking-wide text-foreground-950">
                {typeLabel[current.type]}
              </span>
              <span className="rounded-md border border-background-50/30 bg-background-50/10 px-2 py-1 font-label text-[11px] text-background-100 backdrop-blur">
                {current.region} · {current.year}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-background-50/10 px-2 py-1 font-label text-[11px] font-bold text-accent-300 backdrop-blur">
                <i className="ri-star-fill w-3 h-3 flex items-center justify-center" />
                {current.rating.toFixed(1)}
              </span>
            </div>
            <h2 className="mt-3 font-heading text-2xl font-black leading-snug text-background-50 md:mt-4 md:text-4xl lg:text-5xl">
              {current.title}
            </h2>
            <p className="mt-2 font-label text-[11px] uppercase tracking-[0.16em] text-background-200/80 md:text-xs">
              {current.originalTitle}
            </p>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:mt-4 md:text-[15px]">
              {current.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2.5 md:mt-6">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-accent-500 px-5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line w-4 h-4 flex items-center justify-center" />
                查看剧情介绍
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-background-50/35 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
              >
                更多{typeLabel[current.type]}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 z-20 hidden items-center gap-3 md:flex md:right-8 lg:right-12">
          <span className="font-label text-xs text-background-200">
            0{index + 1} / 0{slides.length}
          </span>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一部"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一部"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-4 z-20 flex items-center justify-center gap-1.5 md:justify-start md:left-8 md:right-auto lg:left-12">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到${slide.title}`}
            className={`h-1 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/45 hover:bg-background-50/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
}